// ===========================================================================
// PatientsTab — Tab 1 del módulo. Lista paginada server-side de pacientes high
// cost. Los filtros viven en la URL (compartible). Orden por defecto: costo
// total descendente (el backend ya entrega ordenado por costo desc).
// ===========================================================================
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Box, Typography, Chip, Stack, Link, Button, Alert } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { getHighCostList, getHighCostCount } from "../../services";
import { formatCurrency } from "../../utils/format";
import { HEALTH_STATUS_COLORS, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, HIGH_COST_BASE_PATH } from "../../common/constants";
import HighCostFilters from "./HighCostFilters";
import CostBreakdownPanel from "./CostBreakdownPanel";

// Lee los filtros desde los query params de la URL.
const readFilters = (sp) => ({
  search: sp.get("search") || "",
  minTotalCost: sp.get("minTotalCost") || "",
  minGlobalIndicator: sp.get("minGlobalIndicator") || "",
  condition: sp.get("condition") || "",
  gender: sp.get("gender") || "",
  locationId: sp.get("locationId") || "",
  pcpName: sp.get("pcpName") || "",
  onlyActive: sp.get("onlyActive") !== "false",
  page: Number(sp.get("page")) || 0,
  pageSize: Number(sp.get("pageSize")) || DEFAULT_PAGE_SIZE,
});

export default function PatientsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [panelCip, setPanelCip] = useState(null);

  // Escribe nuevos valores de filtro en la URL (resetea page salvo cambio de página).
  const updateFilters = useCallback(
    (partial, resetPage = true) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(partial).forEach(([k, v]) => {
          if (v === "" || v === null || v === undefined || (k === "onlyActive" && v === true)) {
            next.delete(k);
          } else {
            next.set(k, String(v));
          }
        });
        if (resetPage) next.delete("page");
        return next;
      });
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => setSearchParams(new URLSearchParams()), [setSearchParams]);

  // Fetch (debounced) cuando cambian los filtros.
  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      const apiFilters = {
        search: filters.search || null,
        minTotalCost: filters.minTotalCost || null,
        minGlobalIndicator: filters.minGlobalIndicator || null,
        condition: filters.condition || null,
        gender: filters.gender || null,
        locationId: filters.locationId || null,
        pcpName: filters.pcpName || null,
        onlyActive: filters.onlyActive,
        page: filters.page + 1, // API es 1-based
        pageSize: filters.pageSize,
      };
      try {
        const [list, total] = await Promise.all([
          getHighCostList(apiFilters),
          getHighCostCount(apiFilters),
        ]);
        if (!active) return;
        setRows(Array.isArray(list?.data) ? list.data : []);
        setRowCount(Number(total) || 0);
      } catch (err) {
        if (!active) return;
        setError("Could not load high cost patients.");
        setRows([]);
        setRowCount(0);
      } finally {
        if (active) setLoading(false);
      }
    }, 350);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [filters]);

  const columns = useMemo(
    () => [
      {
        field: "memberFullName",
        headerName: "Patient",
        flex: 1.4,
        minWidth: 220,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ py: 0.5 }}>
            <Link
              component="button"
              underline="hover"
              onClick={() => navigate(`${HIGH_COST_BASE_PATH}/${encodeURIComponent(params.row.cipId)}`)}
              sx={{ fontWeight: 600, textAlign: "left" }}
            >
              {params.row.memberFullName}
            </Link>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {params.row.age}y · {params.row.gender} · {params.row.cipId}
              {params.row.weeksAbsent > 0 && (
                <Chip
                  label={`Absent ${params.row.weeksAbsent}w`}
                  size="small"
                  sx={{ ml: 0.5, height: 16, fontSize: "0.6rem", opacity: 0.7 }}
                />
              )}
            </Typography>
          </Box>
        ),
      },
      {
        field: "totalCost",
        headerName: "Total cost",
        width: 140,
        sortable: false,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {formatCurrency(params.row.totalCost)}
          </Typography>
        ),
      },
      {
        field: "healthStatus",
        headerName: "Clinical",
        width: 150,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ py: 0.5 }}>
            <Chip
              label={params.row.healthStatus || "—"}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.7rem",
                fontWeight: 600,
                color: "#fff",
                bgcolor: HEALTH_STATUS_COLORS[params.row.healthStatus] || "#9e9e9e",
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              GI {params.row.globalIndicator}
            </Typography>
          </Box>
        ),
      },
      { field: "pcpName", headerName: "PCP", flex: 1, minWidth: 150, sortable: false },
      {
        field: "actions",
        headerName: "",
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            size="small"
            startIcon={<ReceiptLongIcon fontSize="small" />}
            onClick={() => setPanelCip(params.row.cipId)}
            sx={{ textTransform: "none" }}
          >
            View cost
          </Button>
        ),
      },
    ],
    [navigate]
  );

  return (
    <Box>
      <HighCostFilters filters={filters} onChange={updateFilters} onClear={clearFilters} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {rowCount.toLocaleString()} patients · sorted by total cost (desc)
        </Typography>
      </Stack>

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.highCostId ?? row.cipId}
        loading={loading}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={{ page: filters.page, pageSize: filters.pageSize }}
        onPaginationModelChange={(model) =>
          updateFilters({ page: model.page, pageSize: model.pageSize }, false)
        }
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowHeight={() => 56}
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
        }}
      />

      <CostBreakdownPanel cipId={panelCip} open={!!panelCip} onClose={() => setPanelCip(null)} />
    </Box>
  );
}
